import { defineStore } from 'pinia';
import type { AdditionalInfo } from '@/types';
import {
  MESSAGE_DIRECTION,
  PROCESSING_STAGE,
  type MessageDirection,
  type ProcessingStage,
} from '@/constants';

export interface Message {
  id: string;
  text: string;
  direction: MessageDirection;
  fromMe: boolean;
  timestamp: string;
}

export interface AiResponseData {
  translation?: string;
  agentAnswer?: string;
  answer_nl?: string; // deprecated, kept for backward compatibility
  originalMessage?: string;
  additionalInfo?: AdditionalInfo;
  timestamp: string;
  // Marks the response as newly arrived (unread) so UI can highlight it
  isNew?: boolean;
  // Links this response to a specific message via CloudEvent subject
  messageId?: string;
}

export interface AiResponseProcessingState {
  isProcessing: boolean;
  stage?: ProcessingStage;
  // Links processing state to a specific message
  messageId?: string;
  // Retry progress (set while the worker is retrying a failed attempt)
  attempt?: number;
  maxAttempts?: number;
}

// A single entry in the verbose middleware activity feed
export interface ActivityEntry {
  phase: string;
  text: string;
  time: string;
}

// Track AI response state per message
export interface MessageAiState {
  messageId: string;
  isProcessing: boolean;
  stage?: ProcessingStage;
  response?: AiResponseData;
  error?: string;
  retryCount?: number;
}

export const useMessageStore = defineStore('messages', {
  state: () => ({
    messages: [] as Message[],
    isConnected: false,
    backendConnected: false,
    conversationEnded: false,
    aiResponses: [] as AiResponseData[],
    aiResponseError: null as string | null,
    aiResponseProcessing: {
      isProcessing: false,
      stage: undefined,
    } as AiResponseProcessingState,
    // New: Track AI response state per message ID
    messageAiStates: new Map<string, MessageAiState>(),
    currentSummary: null as string | null,
    isSummaryGenerating: false,
    summaryError: null as string | null,
    summaryTimestamp: null as string | null,
    // Verbose middleware activity feed
    currentPhase: null as string | null,
    currentActivityText: null as string | null,
    recentActivity: [] as ActivityEntry[],
    // Resumable-conversation segment state
    pendingResumeDecision: false,
    previousSegmentSummary: null as string | null,
    isPreviousSegmentSummaryGenerating: false,
    segmentMode: 'default',
    ackedBoundaryId: '',
    pendingBoundaryId: '',
    detectedBoundaryId: '',
  }),

  getters: {
    hasAiResponse: (state) => state.aiResponses.length > 0,
    isAiResponseProcessing: (state) => state.aiResponseProcessing.isProcessing,
    hasSummary: (state) => !!state.currentSummary,
    connectionStatus: (state) => ({
      genesys: state.isConnected,
      backend: state.backendConnected,
      bothConnected: state.isConnected && state.backendConnected,
    }),
    // Only show customer messages (we don't render dealer messages in UI)
    customerMessages: (state) =>
      state.messages.filter((m) => m.direction === MESSAGE_DIRECTION.INBOUND),
    // New: Get AI state for a specific message
    getMessageAiState: (state) => (messageId: string) => {
      return state.messageAiStates.get(messageId);
    },
    // New: Check if any message is processing
    hasAnyProcessing: (state) => {
      return Array.from(state.messageAiStates.values()).some(
        (state) => state.isProcessing
      );
    },
    // New: Get all messages with active AI processing
    processingMessages: (state) => {
      return Array.from(state.messageAiStates.values()).filter(
        (state) => state.isProcessing
      );
    },
  },

  actions: {
    addMessage(message: Message) {
      this.messages.push(message);
    },

    clearMessages() {
      this.messages = [];
      this.aiResponses = [];
      this.aiResponseError = null;
      this.clearAiResponseProcessing();
      this.messageAiStates.clear();
      this.clearSummary();
      this.clearActivity();
      this.clearResumeState();
    },

    setConnectionStatus(connected: boolean) {
      this.isConnected = connected;
    },

    setBackendConnectionStatus(connected: boolean) {
      this.backendConnected = connected;
    },

    setConversationEnded(ended: boolean) {
      this.conversationEnded = ended;
    },

    // Legacy method - kept for backward compatibility
    setLatestAiResponse(aiResponse: AiResponseData) {
      // Mark incoming response as new so UI can show a badge
      aiResponse.isNew = true;
      this.aiResponses.unshift(aiResponse);
      this.aiResponseError = null;
    },

    // NEW: Set AI response for a specific message
    setMessageAiResponse(messageId: string, aiResponse: AiResponseData) {
      aiResponse.isNew = true;
      aiResponse.messageId = messageId;

      // Update the message-specific state
      const state = this.messageAiStates.get(messageId) || {
        messageId,
        isProcessing: false,
      };
      state.response = aiResponse;
      state.isProcessing = false;
      state.error = undefined;
      this.messageAiStates.set(messageId, state);

      // Also add to legacy array for backward compatibility
      this.aiResponses.unshift(aiResponse);
      this.aiResponseError = null;

      // Update global processing state for backward compatibility
      this.clearAiResponseProcessing();
    },

    // NEW: Set processing state for a specific message
    setMessageProcessingStatus(
      messageId: string,
      processing: boolean,
      stage?: ProcessingStage,
      retryCount?: number
    ) {
      const state = this.messageAiStates.get(messageId) || {
        messageId,
        isProcessing: false,
      };
      state.isProcessing = processing;
      state.stage = processing
        ? stage || PROCESSING_STAGE.GENERATING
        : undefined;
      if (retryCount !== undefined) {
        state.retryCount = retryCount;
      }
      this.messageAiStates.set(messageId, state);

      // Update global processing state for backward compatibility
      if (processing) {
        this.aiResponseProcessing.isProcessing = true;
        this.aiResponseProcessing.stage = stage || PROCESSING_STAGE.GENERATING;
        this.aiResponseProcessing.messageId = messageId;
      } else {
        // Only clear global processing if no other messages are processing
        const hasOtherProcessing = Array.from(
          this.messageAiStates.values()
        ).some((s) => s.messageId !== messageId && s.isProcessing);
        if (!hasOtherProcessing) {
          this.clearAiResponseProcessing();
        }
      }
    },

    // NEW: Set error for a specific message
    setMessageAiError(messageId: string, error: string) {
      const state = this.messageAiStates.get(messageId) || {
        messageId,
        isProcessing: false,
      };
      state.error = error;
      state.isProcessing = false;
      state.stage = undefined;
      this.messageAiStates.set(messageId, state);

      // Update global error for backward compatibility
      this.aiResponseError = error;
      this.clearAiResponseProcessing();
    },

    // NEW: Clear AI state for a specific message
    clearMessageAiState(messageId: string) {
      this.messageAiStates.delete(messageId);

      // Update global state if needed
      if (!this.messageAiStates.size) {
        this.aiResponseError = null;
        this.clearAiResponseProcessing();
      }
    },

    // Mark a specific AI response (by timestamp) as opened / no longer new
    markAiResponseOpened(timestamp: string) {
      const idx = this.aiResponses.findIndex((r) => r.timestamp === timestamp);
      if (idx !== -1) {
        this.aiResponses[idx].isNew = false;

        // Also update in messageAiStates if it exists
        const messageId = this.aiResponses[idx].messageId;
        if (messageId) {
          const state = this.messageAiStates.get(messageId);
          if (state?.response) {
            state.response.isNew = false;
          }
        }
      }
    },

    // Legacy method - kept for backward compatibility
    setAiResponseError(error: string) {
      this.aiResponseError = error;
      this.clearAiResponseProcessing();
    },

    // Legacy method - kept for backward compatibility
    setAiResponseProcessingStatus(
      processing: boolean,
      stage?: AiResponseProcessingState['stage']
    ) {
      this.aiResponseProcessing.isProcessing = processing;
      this.aiResponseProcessing.stage = stage || PROCESSING_STAGE.GENERATING;

      if (!processing) {
        this.clearAiResponseProcessing();
      }
    },

    clearAiResponseProcessing() {
      this.aiResponseProcessing.isProcessing = false;
      this.aiResponseProcessing.stage = undefined;
    },

    clearAiResponse(preserveProcessing = false) {
      this.aiResponses = [];
      this.aiResponseError = null;
      if (!preserveProcessing) {
        this.clearAiResponseProcessing();
      }
    },

    // Summary actions
    setSummaryGenerating(generating: boolean) {
      this.isSummaryGenerating = generating;
      if (generating) {
        this.summaryError = null; // Clear previous errors when starting
      }
    },

    setSummaryResponse(summary: string, timestamp?: string) {
      this.currentSummary = summary;
      this.isSummaryGenerating = false;
      this.summaryError = null;
      this.summaryTimestamp = timestamp || new Date().toISOString();
    },

    setSummaryError(error: string) {
      this.summaryError = error;
      this.isSummaryGenerating = false;
    },

    clearSummary() {
      this.currentSummary = null;
      this.summaryError = null;
      this.summaryTimestamp = null;
      this.isSummaryGenerating = false;
    },

    // Verbose middleware activity feed
    pushActivity(phase: string, text: string) {
      this.currentPhase = phase;
      this.currentActivityText = text;
      this.recentActivity.unshift({
        phase,
        text,
        time: new Date().toISOString(),
      });
      // Keep only the most recent entries to avoid unbounded growth.
      if (this.recentActivity.length > 10) {
        this.recentActivity.length = 10;
      }
    },

    clearActivity() {
      this.currentPhase = null;
      this.currentActivityText = null;
      this.recentActivity = [];
    },

    // Resumable-conversation segment state
    setSegmentMode(mode: string) {
      this.segmentMode = mode || 'default';
    },

    setAckedBoundaryId(id: string) {
      this.ackedBoundaryId = id || '';
    },

    setDetectedBoundaryId(id: string) {
      this.detectedBoundaryId = id || '';
    },

    setPreviousSegmentSummaryGenerating(generating: boolean) {
      this.isPreviousSegmentSummaryGenerating = generating;
    },

    openResumeDecision(boundaryId: string) {
      this.pendingResumeDecision = true;
      this.pendingBoundaryId = boundaryId;
    },

    setPreviousSegmentSummary(summary: string) {
      this.previousSegmentSummary = summary;
      this.isPreviousSegmentSummaryGenerating = false;
    },

    resolveResumeDecision(mode: string) {
      this.pendingResumeDecision = false;
      this.segmentMode = mode || 'default';
      this.ackedBoundaryId = this.pendingBoundaryId;
    },

    clearResumeState() {
      this.pendingResumeDecision = false;
      this.previousSegmentSummary = null;
      this.isPreviousSegmentSummaryGenerating = false;
      this.segmentMode = 'default';
      this.ackedBoundaryId = '';
      this.pendingBoundaryId = '';
      this.detectedBoundaryId = '';
    },
  },
});
